import React, { useEffect, useMemo, useRef, useState } from "react";
import api from "../../api";
import { toast } from "sonner";
import { Compass, Plus, Trash2, Save, Loader2, Download, Upload } from "lucide-react";

const AGES = ["4-8", "9-13"];

function ScenarioRow({ theme, ageGroup, order, existing, onSaved }) {
  const [situation, setSituation] = useState(existing?.situation || "");
  const [prompt, setPrompt] = useState(existing?.prompt || "What would you do?");
  const [busy, setBusy] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const isNew = !existing;

  const save = async () => {
    if (!situation.trim()) { toast.error("Situation cannot be empty."); return; }
    setBusy(true);
    try {
      if (isNew) {
        await api.post("/admin/scenarios", {
          theme_id: theme.id, age_group: ageGroup, order,
          situation: situation.trim(),
          prompt: prompt.trim() || "What would you do?",
        });
        toast.success(`Scenario ${order} added`);
      } else {
        await api.patch(`/admin/scenarios/${existing.id}`, {
          situation: situation.trim(),
          prompt: prompt.trim() || "What would you do?",
        });
        toast.success(`Scenario ${order} updated`);
      }
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save");
    } finally { setBusy(false); }
  };

  const del = async () => {
    if (!existing) return;
    if (!window.confirm(`Delete scenario ${order}? Any families who already answered will keep their responses.`)) return;
    setDeleting(true);
    try {
      await api.delete(`/admin/scenarios/${existing.id}`);
      toast.success("Deleted");
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not delete");
    } finally { setDeleting(false); }
  };

  return (
    <div className="rounded-2xl border border-black/5 bg-white/60 p-4"
         data-testid={`admin-scenario-${theme.slug}-${ageGroup}-${order}`}>
      <div className="flex items-center justify-between mb-2">
        <div className="text-xs uppercase tracking-widest text-muted-foreground">
          Scenario {order} · Age {ageGroup}
        </div>
        {!isNew && (
          <button onClick={del} disabled={deleting}
                  className="text-xs text-destructive hover:underline flex items-center gap-1"
                  data-testid={`admin-scenario-delete-${theme.slug}-${ageGroup}-${order}`}>
            {deleting ? <Loader2 className="w-3 h-3 animate-spin"/> : <Trash2 className="w-3 h-3"/>}
            Delete
          </button>
        )}
      </div>
      <textarea
        className="field min-h-[80px] resize-none text-sm mb-2"
        placeholder={`e.g. "You lose your favorite toy and your sibling laughs at you."`}
        value={situation}
        onChange={(e) => setSituation(e.target.value)}
        data-testid={`admin-scenario-situation-${theme.slug}-${ageGroup}-${order}`}
      />
      <input
        className="field text-sm mb-3"
        placeholder="Prompt (default: What would you do?)"
        value={prompt}
        onChange={(e) => setPrompt(e.target.value)}
        data-testid={`admin-scenario-prompt-${theme.slug}-${ageGroup}-${order}`}
      />
      <button onClick={save} disabled={busy} className="btn-primary text-sm px-4 py-2"
              data-testid={`admin-scenario-save-${theme.slug}-${ageGroup}-${order}`}>
        {busy ? <Loader2 className="w-3 h-3 animate-spin"/> : <><Save className="w-3 h-3"/> {isNew ? "Add" : "Update"}</>}
      </button>
    </div>
  );
}

export default function AdminScenarios() {
  const [themes, setThemes] = useState([]);
  const [scenarios, setScenarios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const fileRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const [t, s] = await Promise.all([
        api.get("/admin/themes"),
        api.get("/admin/scenarios"),
      ]);
      setThemes(t.data);
      setScenarios(s.data);
    } catch (err) {
      toast.error("Could not load");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const downloadTemplate = async () => {
    try {
      const r = await api.get("/admin/scenarios/template", { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = "scenarios_template.xlsx";
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error("Could not download template");
    }
  };

  const uploadFile = async (file) => {
    if (!file) return;
    setUploading(true);
    setUploadResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/admin/scenarios/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setUploadResult(r.data);
      if (r.data.errors && r.data.errors.length > 0) {
        toast.error(`Upload had ${r.data.errors.length} error(s). Fix and re-upload.`);
      } else {
        toast.success(`Uploaded ${r.data.published} scenario(s).`);
        await load();
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const byKey = useMemo(() => {
    const m = {};
    scenarios.forEach((s) => {
      m[`${s.theme_id}::${s.age_group}::${s.order}`] = s;
    });
    return m;
  }, [scenarios]);

  return (
    <div className="max-w-4xl">
      <div className="flex items-start gap-3 mb-4">
        <Compass className="w-6 h-6 text-[hsl(var(--neu-orange))] mt-1"/>
        <div className="flex-1">
          <h1 className="font-display text-3xl">Journey Scenarios</h1>
          <p className="text-sm text-muted-foreground">
            Author 3 baseline scenarios per theme × age group. Parents see the same scenarios before Week 1 and after Week 4 to compare shifts in their child&apos;s thinking.
          </p>
        </div>
      </div>

      {/* Bulk actions */}
      <div className="card-soft p-5 mb-6" data-testid="admin-scenarios-bulk">
        <div className="mb-3">
          <div className="text-xs uppercase tracking-widest text-muted-foreground">How to author scenarios</div>
          <div className="text-sm text-muted-foreground mt-1">
            The recommended way is to fill columns <code className="bg-white/60 px-1 rounded text-xs">scenario_1</code>, <code className="bg-white/60 px-1 rounded text-xs">scenario_2</code> and <code className="bg-white/60 px-1 rounded text-xs">scenario_3</code> on the <b>Week 1 row</b> of each (theme × age group) in the <b>Weekly Content</b> Excel. That single upload publishes stories, questions, activity, reflections and scenarios together.
          </div>
          <div className="text-sm text-muted-foreground mt-2">
            You can also edit any scenario inline below, or use the standalone scenarios-only Excel if you just want to tweak scenarios.
          </div>
        </div>
        <div className="flex flex-wrap gap-3">
          <button onClick={downloadTemplate}
                  className="btn-ghost text-sm px-4 py-2"
                  data-testid="admin-scenarios-download-template">
            <Download className="w-4 h-4"/> Download scenarios-only Excel
          </button>
          <input ref={fileRef} type="file" accept=".xlsx"
                  onChange={(e) => uploadFile(e.target.files?.[0])}
                  className="hidden"
                  data-testid="admin-scenarios-file-input"/>
          <button onClick={() => fileRef.current?.click()} disabled={uploading}
                  className="btn-ghost text-sm px-4 py-2"
                  data-testid="admin-scenarios-upload-btn">
            {uploading ? <Loader2 className="w-4 h-4 animate-spin"/> : <><Upload className="w-4 h-4"/> Upload scenarios-only Excel</>}
          </button>
        </div>
        {uploadResult && (
          <div className="mt-4 space-y-2" data-testid="admin-scenarios-upload-result">
            <div className="text-xs">
              Validated: <b>{uploadResult.validated_rows || 0}</b>
              {typeof uploadResult.published === "number" && <> · Published: <b>{uploadResult.published}</b></>}
              {uploadResult.errors?.length > 0 && <> · Errors: <b className="text-destructive">{uploadResult.errors.length}</b></>}
            </div>
            {uploadResult.errors?.length > 0 && (
              <div className="rounded-xl bg-destructive/10 border border-destructive/20 p-3">
                <div className="text-xs font-medium text-destructive mb-1">Errors — fix these rows and re-upload:</div>
                <ul className="text-xs text-destructive space-y-1 list-disc list-inside">
                  {uploadResult.errors.slice(0, 8).map((e, i) => (
                    <li key={i}>Row {e.row}: {e.error}</li>
                  ))}
                  {uploadResult.errors.length > 8 && (
                    <li>… and {uploadResult.errors.length - 8} more.</li>
                  )}
                </ul>
              </div>
            )}
          </div>
        )}
      </div>

      {loading ? (
        <div className="text-muted-foreground">Loading…</div>
      ) : (
        <div className="space-y-8">
          {themes.map((theme) => (
            <div key={theme.id} className="card-soft p-5" data-testid={`admin-scenarios-theme-${theme.slug}`}>
              <div className="flex items-baseline justify-between mb-4">
                <div>
                  <div className="text-[11px] uppercase tracking-widest text-muted-foreground">
                    Month {theme.month_number || "—"} · {theme.status}
                  </div>
                  <h2 className="font-display text-2xl">{theme.title}</h2>
                </div>
              </div>
              {AGES.map((ag) => (
                <div key={ag} className="mb-5">
                  <div className="text-xs font-medium mb-3">Age group {ag}</div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    {[1, 2, 3].map((ord) => {
                      const existing = byKey[`${theme.id}::${ag}::${ord}`];
                      return (
                        <ScenarioRow key={`${theme.id}-${ag}-${ord}`}
                                      theme={theme} ageGroup={ag} order={ord}
                                      existing={existing} onSaved={load}/>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
